import React, { useState } from "react";
import { DataGrid, GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useQuery } from "react-query";
import { getCourses } from "../api/api";
import { CourseDescriptionComponent } from "./CourseDescriptionComponent";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Close as CloseIcon, Info as InfoIcon } from "@mui/icons-material";

export const TableComponent = () => {
  const [courseId, setCourseId] = useState<string>();
  const [open, setOpen] = useState<boolean>(false);
  const { data, isLoading, error } = useQuery("courses", getCourses);

  const columns: GridColDef[] = [
    {
      field: "Ajankohta",
      headerName: "Ajankohta",
      width: 200,
      renderHeader: () => (
        <Typography variant="subtitle2" fontWeight={600}>
          Ajankohta
        </Typography>
      ),
    },
    {
      field: "PostitoimipaikkaListassa",
      headerName: "Paikka",
      width: 130,
      renderHeader: () => (
        <Typography variant="subtitle2" fontWeight={600}>
          Paikka
        </Typography>
      ),
    },
    {
      field: "Nimi",
      headerName: "Kurssin nimi",
      width: 500,
      renderHeader: () => (
        <Typography variant="subtitle2" fontWeight={600}>
          Kurssin nimi
        </Typography>
      ),
    },
    {
      field: "TapahtumaID",
      headerName: "Lisätiedot",
      width: 150,
      renderHeader: () => (
        <Typography variant="subtitle2" fontWeight={600}>
          Lisätiedot
        </Typography>
      ),
      renderCell: (params) => (
        <IconButton
          size="small"
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            setCourseId(params.value);
            setOpen(true);
          }}
          sx={{ ml: 1 }}
        >
          <InfoIcon />
        </IconButton>
      ),
    },
  ];

  const rows =
    data?.map((item, idx) => ({
      id: idx,
      ...item,
    })) || [];

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Virhe kurssien latauksessa. Yritä myöhemmin uudelleen.
      </Alert>
    );
  }

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        initialState={{
          pagination: {
            pageSize: 20,
          },
        }}
        disableSelectionOnClick
        onRowClick={(params: GridRowParams) => {
          setCourseId(params.row.TapahtumaID);
          setOpen(true);
        }}
        sx={{
          border: "none",
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #e0e0e0",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#f8f9fa",
            borderBottom: "2px solid #1976d2",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: "#f5f5f5",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
        }}
      />

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setCourseId(undefined);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "primary.main",
            color: "primary.contrastText",
          }}
        >
          <Typography variant="h6">Kurssin lisätiedot</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => {
              setOpen(false);
              setCourseId(undefined);
            }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, minHeight: 400 }}>
          {courseId && <CourseDescriptionComponent courseId={courseId} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
